import { useState } from "react";
import { ExtendWindow } from "../components/ExtendWindow.js";
import { ApiError, type Goal, type GoalView, type Verdict, api, clausesOf, label } from "../api.js";

/**
 * Кадр 2 в компоновке В: наверху состояние и действие, запертое свёрнуто в строки,
 * критерий с доказательством раскрыт всегда — иначе он превращается в строку настроек.
 *
 * ⚠️ Идущий протокол показан СТРОКОЙ внутри той же панели, а не своей карточкой —
 * как в эталоне. Отдельной карточкой на этом экране одновременно видно пять стеклянных
 * слоёв: плитки, запертое, протокол, касания и панель вкладок. Лимит языка — четыре.
 */

type Props = { view: GoalView; goals: Goal[]; onChanged: () => void; onBack: () => void };

export function GoalCardScreen({ view, goals, onChanged, onBack }: Props): React.JSX.Element {
  const { goal, protocols, touches, forecasts, verdicts, recovery, revisions } = view;
  const [error, setError] = useState<string | null>(null);
  const running = protocols.find((protocol) => protocol.status === "running");
  const machineRule = goal.machine_rule ? (JSON.parse(goal.machine_rule) as { metric: string; op: string; value: number }) : null;

  /**
   * Правило кода, помеченное overridable, не запрещает действие — оно требует причину.
   * Причина уходит в event_log: правило, нарушенное молча, ничему не учит.
   */
  async function act(run: (overrideReason?: string) => Promise<unknown>) {
    setError(null);
    try {
      await run();
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.overridable) {
        const reason = window.prompt(`${caught.message}\n\nПричина обхода (попадёт в журнал):`);
        if (!reason) return;
        try {
          await run(reason);
          onChanged();
        } catch (retry) {
          setError(retry instanceof Error ? retry.message : "не вышло");
        }
        return;
      }
      setError(caught instanceof Error ? caught.message : "не вышло");
    }
  }

  const forecast = forecasts.at(0);
  const judgeable = Boolean(goal.criteria_locked_at) && goal.status === "active";

  return (
    <>
      <div className="nav">
        <button className="linklike back" onClick={onBack}>
          ‹ Цели
        </button>
      </div>
      <h1 className="title">{goal.title}</h1>
      <div className="axes">
        <span className="state">{label(goal.status)}</span>
        <span className="chip axis horizon">горизонт: {label(goal.horizon_bucket)}</span>
        <span className="chip axis domain">сфера: {label(goal.domain)}</span>
        {goal.target_date && <span className="chip axis">до {goal.target_date}</span>}
      </div>

      <section className="tiles">
        <div className="tile">
          <div className="k">Твой прогноз</div>
          {forecast?.prob !== null && forecast?.prob !== undefined ? (
            <div className="v">{Math.round(forecast.prob * 100)}<small>%</small></div>
          ) : (
            <div className="none">не записан</div>
          )}
          <span className="n">{forecast ? `от ${forecast.made_at.slice(0, 10)}` : "прогноза нет"}</span>
        </div>
        <div className="tile">
          <div className="k">Прогноз агента</div>
          <div className="none">своей вероятности нет</div>
          <span className="n">агент появится на этапе 4</span>
        </div>
        <div className="tile">
          <div className="k">Приверженность</div>
          {running ? (
            <>
              <div className="v">
                {running.adherence.done}
                <small> из {running.adherence.planned}</small>
              </div>
              <span className="n">n = {running.adherence.weeks} нед.</span>
            </>
          ) : (
            <div className="none">протокол не идёт</div>
          )}
        </div>
        <div className="tile">
          <div className="k">Возврат после срыва</div>
          {recovery.median_days === null ? (
            <div className="none">срывов ещё не было</div>
          ) : (
            <>
              <div className="v">{recovery.median_days}<small> дн.</small></div>
              <span className="n">n = {recovery.n} · без возврата {recovery.without_return}</span>
            </>
          )}
        </div>
      </section>

      {error && <p className="error">{error}</p>}

      <div className="act">
        {goal.status !== "active" && goal.status !== "achieved" && (
          <button
            className="btn primary"
            onClick={() =>
              void act((reason) =>
                api.post(`/api/goals/${goal.id}/activate`, reason ? { override_reason: reason } : {})
              )
            }
          >
            Запереть и запустить
          </button>
        )}
        {/* Провал закрывается вручную: система его не выводит, но записать обязана дать.
            Без этой кнопки у активной цели был ровно один выход — «достигнута», и метрика
            отсева умела только хвалить. */}
        {goal.status === "active" && (
          <button
            className="btn ghost"
            onClick={() =>
              void act(async () => {
                const reason = window.prompt("Почему цель не состоялась? Причина попадёт в запись.");
                if (!reason) return;
                await api.post(`/api/goals/${goal.id}/abandon`, { reason });
              })
            }
          >
            Закрыть как провал
          </button>
        )}
        {(goal.status === "cooling" || goal.status === "candidate") && (
          <button
            className="btn ghost"
            onClick={() =>
              void act(async () => {
                const reason = window.prompt("Почему убиваем в отлёжке? (это успех отсева, а не провал)");
                if (!reason) return;
                await api.post(`/api/goals/${goal.id}/kill`, { reason });
              })
            }
          >
            Убить в отлёжке
          </button>
        )}
      </div>

      <div className="sec">Заперто до старта</div>
      <section className="rows">
        <div className="row open">
          <div className="head-row">
            <span className="k">Критерий и доказательство</span>
            <span className="seal">{goal.criteria_locked_at ? `заперты ${goal.criteria_locked_at.slice(0, 10)}` : "ещё не заперты"}</span>
          </div>
          <div className="body">
            {goal.success_criterion}
            <span className="proof">Доказательство: {goal.proof_rule}</span>
            {machineRule && (
              <span className="judge">
                правило численное ({machineRule.metric} {machineRule.op} {machineRule.value}) → судит код
              </span>
            )}
          </div>
        </div>
        <div className="row">
          <span>Ревизии и обходы</span>
          <span className="sub">{revisions.length}</span>
        </div>
        <div className="row">
          <span className="two-line">
            Награда: {goal.reward ?? "не назначена"}
            <span className="hint">{goal.reward_unlocked_at ? `открыта ${goal.reward_unlocked_at.slice(0, 10)}` : "за воротами до вердикта"}</span>
          </span>
        </div>
        <ChainRow view={view} goals={goals} onChanged={onChanged} />

        {running && (
          <div className="row open proto">
            <div className="head-row">
              <span className="k">Протокол</span>
              {/* Дата берётся локальная, с устройства владельца: это подпись состояния,
                  а не правило. Само решение — исход протокола — живёт на «Сегодня»,
                  и границу окна там считает сервер по OWNER_TZ. */}
              <span className="seal">
                {running.window_end < new Date().toLocaleDateString("sv") ? "ждёт исхода" : "идёт"}
              </span>
            </div>
            <div className="body">
              {running.hypothesis}
              <span className="proof">
                окно {running.window_start} — {running.window_end} · правило успеха: {running.success_rule}
              </span>
              {/* Связь с предшественником — часть записи, а не украшение: без неё непонятно,
                  почему приверженность считается с нуля, хотя работа шла давно. */}
              {running.supersedes_id && (
                <span className="lock">
                  преемник предыдущего протокола · правило успеха и метрика унаследованы дословно
                </span>
              )}
              <span className="lock">предрегистрировано {running.preregistered_at?.slice(0, 10)}</span>
              {/* Продлить можно, пока протокол идёт, — не дожидаясь, пока окно кончится
                  и «Сегодня» спросит исход. Форма та же самая: правило «только вперёд»
                  и обязательная причина живут в одном месте. */}
              <ExtendWindow protocolId={running.id} windowEnd={running.window_end} onChanged={onChanged} />
            </div>
          </div>
        )}
      </section>

      {!running && (
        <>
          <div className="sec">Протокол</div>
          <ProtocolStarter goalId={goal.id} protocols={protocols} onChanged={onChanged} />
        </>
      )}

      <div className="sec">Касания</div>
      <section className="feed">
        {touches.length === 0 && <div className="t-row">пока ни одного</div>}
        {touches.map((touch) => (
          <div className="t-row" key={touch.id}>
            <span className="d">{touch.occurred_at.slice(5, 10)}</span>
            <span className="m">
              <span className={`val ${touch.kind === "skipped" ? "miss-mark" : ""}`}>
                {touch.kind === "skipped" ? "пропуск" : touch.metric_value !== null ? `${touch.metric_value}` : "сделано"}
              </span>{" "}
              <span className="ob">· {label(touch.obstacle_code)}</span>
            </span>
          </div>
        ))}
      </section>

      {/* Суд и вердикты — одна панель: вердикт это исход суда, а не соседний раздел.
          Раздельными панелями на этом экране одновременно видно пять слоёв. */}
      {(judgeable || verdicts.length > 0) && (
        <>
          <div className="sec">Суд</div>
          <section className="card court">
            {judgeable && (
              <JudgeBlock goalId={goal.id} machineRule={machineRule} onChanged={onChanged} />
            )}
            {verdicts.map((verdict) => (
              <VerdictBlock key={verdict.id} verdict={verdict} onChanged={onChanged} />
            ))}
          </section>
        </>
      )}
    </>
  );
}

/**
 * Цепочка в карточке цели (решение 3 CONCEPT — иерархия в обе стороны).
 *
 * Уровень ровно один, и это правило кода, а не соглашение: звено не может быть родителем.
 * Поэтому у цели ровно одно из трёх состояний — она длинная и к ней ведут, она звено,
 * или она сама по себе. Объявить цепочку можно только из звена: так её и думают —
 * «эта цель ведёт к той», а не наоборот.
 */
function ChainRow({
  view,
  goals,
  onChanged
}: {
  view: GoalView;
  goals: Goal[];
  onChanged: () => void;
}): React.JSX.Element {
  const { goal, chain } = view;
  const [parentId, setParentId] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (chain.parent) {
    return (
      <div className="row">
        <span className="two-line">
          Цепочка «{chain.parent.title}»
          {/* Дата объявления живёт у ДЛИННОЙ цели, а не у звена: цепочку объявляют
              один раз сверху, и `declareChain` пишет её родителю. */}
          <span className="hint">
            {chain.parent.chain_declared_at
              ? `объявлена ${chain.parent.chain_declared_at.slice(0, 10)}`
              : "звено цепочки"}
          </span>
        </span>
        <span className="sub">{label(chain.parent.horizon_bucket)}</span>
      </div>
    );
  }

  if (chain.children.length > 0) {
    return (
      <div className="row">
        <span className="two-line">
          К этой цели ведут звенья
          <span className="hint">закрытые короткие идут контекстом, а не зачётом</span>
        </span>
        <span className="sub num">{chain.children.length}</span>
      </div>
    );
  }

  // Родителем может быть только цель более длинного горизонта и не звено само:
  // оба правила проверяет сервер, здесь мы просто не предлагаем заведомо невозможное.
  const order = ["week", "month", "quarter", "year_plus"];
  const candidates = goals.filter(
    (candidate) =>
      candidate.id !== goal.id &&
      !candidate.parent_goal_id &&
      order.indexOf(candidate.horizon_bucket) > order.indexOf(goal.horizon_bucket)
  );

  async function declare() {
    if (!parentId) return;
    setError(null);
    try {
      await api.post(`/api/goals/${parentId}/chain`, { child_ids: [goal.id] });
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не объявилось");
    }
  }

  return (
    <div className="row open">
      <div className="head-row">
        <span className="k">Цепочка</span>
        <span className="seal">не объявлена</span>
      </div>
      {candidates.length === 0 ? (
        <div className="body">
          <span className="hint">
            Более длинной цели, к которой эта могла бы вести, пока нет. Цепочка объявляется
            вперёд, а не задним числом.
          </span>
        </div>
      ) : (
        <div className="chain-form">
          <select
            className="field"
            value={parentId}
            onChange={(event) => setParentId(event.target.value)}
            aria-label="длинная цель"
          >
            <option value="">к какой длинной цели ведёт</option>
            {candidates.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.title}
              </option>
            ))}
          </select>
          <button className="btn" disabled={!parentId} onClick={() => void declare()}>
            Объявить
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function ProtocolStarter({
  goalId,
  protocols,
  onChanged
}: {
  goalId: string;
  protocols: GoalView["protocols"];
  onChanged: () => void;
}): React.JSX.Element {
  const planned = protocols.find((protocol) => protocol.status === "planned");
  const [hypothesis, setHypothesis] = useState("");
  const [perWeek, setPerWeek] = useState("3");
  const [metric, setMetric] = useState("distance_km");
  const [rule, setRule] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setError(null);
    try {
      const today = new Date();
      const end = new Date(today.getTime() + 42 * 86_400_000);
      await api.post("/api/protocols", {
        goal_id: goalId,
        hypothesis,
        cadence: { kind: "per_week_count", value: Number(perWeek) },
        metric_name: metric,
        metric_direction: "up",
        window_start: today.toISOString().slice(0, 10),
        window_end: end.toISOString().slice(0, 10),
        success_rule: rule
      });
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "не вышло");
    }
  }

  async function launch(id: string) {
    setError(null);
    try {
      await api.post(`/api/protocols/${id}/preregister`);
      await api.post(`/api/protocols/${id}/start`);
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.overridable) {
        const reason = window.prompt(`${caught.message}\n\nПричина обхода:`);
        if (!reason) return;
        await api.post(`/api/protocols/${id}/start`, { override_reason: reason });
        onChanged();
        return;
      }
      setError(caught instanceof Error ? caught.message : "не вышло");
    }
  }

  if (planned) {
    return (
      <section className="card">
        <p className="what">{planned.hypothesis}</p>
        <p className="hint">правило успеха: {planned.success_rule}</p>
        <button className="btn primary" onClick={() => void launch(planned.id)}>
          Предрегистрировать и запустить
        </button>
        {error && <p className="error">{error}</p>}
      </section>
    );
  }

  return (
    <section className="card form">
      <label>
        Гипотеза
        <input value={hypothesis} onChange={(event) => setHypothesis(event.target.value)} placeholder="если бегать 3×/нед…" />
      </label>
      <div className="triple">
        <label>
          Раз в неделю
          <input value={perWeek} onChange={(event) => setPerWeek(event.target.value)} inputMode="numeric" />
        </label>
        <label>
          Метрика
          <input value={metric} onChange={(event) => setMetric(event.target.value)} />
        </label>
      </div>
      <label>
        Правило успеха <span className="req">пишется до старта</span>
        <input value={rule} onChange={(event) => setRule(event.target.value)} placeholder="два подряд объёма ≥ 25 км" />
      </label>
      {error && <p className="error">{error}</p>}
      <button className="btn" onClick={() => void create()}>
        Создать протокол
      </button>
    </section>
  );
}

function JudgeBlock({
  goalId,
  machineRule,
  onChanged
}: {
  goalId: string;
  machineRule: { metric: string; op: string; value: number } | null;
  onChanged: () => void;
}): React.JSX.Element {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function judgeByAgent() {
    setError(null);
    setBusy(true);
    try {
      await api.post(`/api/goals/${goalId}/judge-agent`, {
        evidence: [{ kind: "note", value: value || "предъявлено" }]
      });
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "судья не ответил");
    } finally {
      setBusy(false);
    }
  }

  async function judge(decision?: "accepted" | "rejected") {
    setError(null);
    // ⚠️ Пустое поле давало Number("") = 0, и в леджер уходил НАСТОЯЩИЙ отказ
    // с измерением ноль — вердикт о том, чего не измеряли. Промах пальцем становился
    // записью навсегда.
    if (machineRule && !Number.isFinite(Number(value.replace(",", ".")))) {
      setError(`Нужно измерение метрики ${machineRule.metric} — числом`);
      return;
    }
    if (machineRule && value.trim() === "") {
      setError(`Нужно измерение метрики ${machineRule.metric} — числом`);
      return;
    }
    try {
      await api.post(`/api/goals/${goalId}/judge`, {
        evidence: machineRule
          ? // Имя метрики прикладывается всегда: судья сверяет ЧТО измерено, а не только сколько.
            [{ kind: "metric", metric: machineRule.metric, value: Number(value.replace(",", ".")) }]
          : [{ kind: "note", value: value || "предъявлено" }],
        ...(decision ? { owner_decision: decision } : {})
      });
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "не вышло");
    }
  }

  return (
    <div className="form judge-form">
      <label>
          {machineRule ? `Измерение: ${machineRule.metric}` : "Доказательство"}
          <input value={value} onChange={(event) => setValue(event.target.value)} placeholder={machineRule ? "8.2" : "что предъявляешь"} />
        </label>
        {machineRule ? (
          <button className="btn primary" onClick={() => void judge()}>
            Судить кодом
          </button>
        ) : (
          <>
            <div className="act">
              <button className="btn primary" onClick={() => void judge("accepted")}>
                Принять
              </button>
              <button className="btn" onClick={() => void judge("rejected")}>
                Не принять
              </button>
            </div>
            {/* Агента зовут только сюда: численное правило судит код, и кнопки здесь нет. */}
            <button className="btn ghost" disabled={busy} onClick={() => void judgeByAgent()}>
              {busy ? "судья читает правило…" : "Отдать судье-агенту"}
            </button>
          </>
        )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/**
 * Вердикт с разбором правила на пункты. Пункты замёрзли в снимке в момент суда, поэтому
 * «не закрыт пункт 2» продолжает значить одно и то же и через год, когда правило перепишут.
 *
 * Апелляция стоит рядом намеренно: судья, которого нельзя оспорить, — это не судья,
 * а препятствие. Но причина обязана быть письменной, и доля переопределений копится.
 */
function VerdictBlock({ verdict, onChanged }: { verdict: Verdict; onChanged: () => void }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const clauses = clausesOf(verdict);
  const effective = verdict.final_decision ?? verdict.decision;

  async function appeal(final: "accepted" | "rejected" | "needs_more") {
    setError(null);
    try {
      await api.post(`/api/verdicts/${verdict.id}/appeal`, { reason, final_decision: final });
      setOpen(false);
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "апелляция не прошла");
    }
  }

  return (
    <div className="verdict">
      <div className="prop-head">
        <span className={effective === "accepted" ? "" : "miss-mark"}>{label(effective)}</span>
        <span className="lock">
          судил {label(verdict.judged_by)}
          {verdict.model ? ` · ${verdict.model}` : ""}
        </span>
      </div>

      {clauses.length > 0 && (
        <ol className="clauses">
          {clauses.map((clause, index) => (
            <li key={index} className={clause.status === "covered" ? "" : "miss-mark"}>
              {clause.text} — {label(clause.status)}
              {clause.evidence_index !== null && ` (доказательство ${clause.evidence_index + 1})`}
              {clause.note && <span className="hint">{clause.note}</span>}
            </li>
          ))}
        </ol>
      )}

      {clauses.length === 0 && verdict.reasoning && <p className="hint">{verdict.reasoning}</p>}

      {verdict.appealed === 1 && (
        <p className="lock">
          Переопределено владельцем: {verdict.appeal_reason}. Исходное решение агента —{" "}
          {label(verdict.decision)}, оно остаётся в записи.
        </p>
      )}

      {verdict.appealed === 0 && !open && (
        <button className="btn ghost" onClick={() => setOpen(true)}>
          Не согласен
        </button>
      )}

      {open && (
        <div className="form">
          <label>
            Почему судья неправ
            <input
              value={reason}
              placeholder="причина попадёт в запись"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="act">
            <button className="btn primary" onClick={() => void appeal("accepted")}>
              Всё же принять
            </button>
            <button className="btn" onClick={() => void appeal("rejected")}>
              Всё же отклонить
            </button>
          </div>
          {error && <p className="error">{error}</p>}
        </div>
      )}
    </div>
  );
}
